import { useUserProfilesMutation } from 'lib/services/userProfile.service'
import type React from 'react'
import type { ReactNode, JSX } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { UserProfileContext } from '../contexts/userProfile.context'
import type { IUserProfile } from 'models/userProfiles'
import Loader from 'components/loader'
import type { UserProfileContextValue } from './common'
import { UserProfileStatus } from './common'
import { useLoggedInUserProfileContext } from 'lib/contexts/loggedInUserProfile.context'
import { updateContextValues } from 'lib/interceptor/axios.interceptor'
import { useNavigate } from 'react-router-dom'
import useVenueLists from 'pages/organization/venuelists/useVenueList'

interface UserProfileProviderProperties {
	children: ReactNode
}

const PageLoader = () => (
	<div className='flex h-screen items-center justify-center'>
		<Loader />
	</div>
)

const UserProfileProvider: React.FC<UserProfileProviderProperties> = ({
	children
}): JSX.Element => {
	const [userProfileStatus, setUserProfileStatus] = useState<UserProfileStatus>(
		UserProfileStatus.initial
	)
	const {
		userProfile: loggedInUserProfile,
		userProfileStatus: loggedInUserProfileStatus
	} = useLoggedInUserProfileContext()

	const [userProfile, setUserProfile] = useState<IUserProfile>()
	const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(
		null
	)
	const { venueLists } = useVenueLists(userProfile?.organizationId ?? '')
	const { mutateAsync: updateUserProfile } = useUserProfilesMutation()
	const navigate = useNavigate()

	useEffect(() => {
		if (loggedInUserProfile && !impersonatedUserId) {
			setUserProfile(loggedInUserProfile)
			setUserProfileStatus(loggedInUserProfileStatus)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [impersonatedUserId, loggedInUserProfile])

	const resetImpersonation = useCallback(() => {
		setImpersonatedUserId(null)
		setUserProfile(loggedInUserProfile)
		updateContextValues(loggedInUserProfile?.id ?? '')
		navigate('/')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedInUserProfile])

	const memoizedValue: UserProfileContextValue = useMemo(() => {
		const useUpdatePreviewExperience = async ({
			userProfileId,
			previewExperienceKey,
			enable
		}: {
			userProfileId?: string
			previewExperienceKey: string
			enable: boolean
		}) => {
			const data = await updateUserProfile({
				profileId: userProfileId ?? userProfile?.id ?? '',
				previewExperienceKey,
				enable: enable ? 'true' : 'false'
			})
			return data
		}

		return {
			userProfile,
			setUserProfile,
			userProfileStatus,
			setUserProfileStatus,
			useUpdatePreviewExperience,
			setImpersonatedUserId,
			impersonatedUserId,
			resetImpersonation,
			venueLists
		}
	}, [
		impersonatedUserId,
		resetImpersonation,
		updateUserProfile,
		userProfile,
		userProfileStatus,
		venueLists
	])

	return (
		<UserProfileContext.Provider value={memoizedValue}>
			{userProfile ? children : <PageLoader />}
		</UserProfileContext.Provider>
	)
}

export default UserProfileProvider
